import type { ProgramProgressLikeRow } from './annualProgressFieldDisplay';

type AnnualProgramDisplayOverlayEntry = {
  scopeId: string;
  updatedAt: number;
  row: ProgramProgressLikeRow;
};

type AnnualProgramDisplayOverlayPayload = {
  byAnnualKey: Record<string, AnnualProgramDisplayOverlayEntry>;
};

const STORAGE_PREFIX = 'cp:annual-program-display-overlay:v1:';
export const ANNUAL_PROGRAM_DISPLAY_OVERLAY_CHANGED_EVENT = 'cp:annual-program-display-overlay-changed';

function storageKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): string {
  return `${STORAGE_PREFIX}${workspaceId.trim()}:${academicYear}:${termNumber}:${gradeId.trim()}`;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function parsePayload(raw: string | null): AnnualProgramDisplayOverlayPayload {
  if (!raw) return { byAnnualKey: {} };
  try {
    const parsed = JSON.parse(raw) as AnnualProgramDisplayOverlayPayload;
    const byAnnualKey =
      parsed && typeof parsed === 'object' && parsed.byAnnualKey && typeof parsed.byAnnualKey === 'object'
        ? parsed.byAnnualKey
        : {};
    return { byAnnualKey };
  } catch {
    return { byAnnualKey: {} };
  }
}

function emitChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(ANNUAL_PROGRAM_DISPLAY_OVERLAY_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

function trimLikeRow(row: ProgramProgressLikeRow): ProgramProgressLikeRow {
  return {
    unit_text: (row.unit_text ?? '').trim(),
    learning_text: (row.learning_text ?? '').trim(),
    page_text: (row.page_text ?? '').trim(),
    assistant_page_text: (row.assistant_page_text ?? '').trim(),
    current_period_text: (row.current_period_text ?? '').trim(),
    total_period_text: (row.total_period_text ?? '').trim(),
    material_text: (row.material_text ?? '').trim(),
    record_cross_curricular_text: (row.record_cross_curricular_text ?? '').trim(),
    record_memo_text: (row.record_memo_text ?? '').trim(),
    record_evaluation_text: (row.record_evaluation_text ?? '').trim(),
    record_class_text: (row.record_class_text ?? '').trim(),
    record_member_group_id: row.record_member_group_id ?? null,
    bg_color_by_field: row.bg_color_by_field ?? {},
  };
}

function bgSignature(row: ProgramProgressLikeRow): string {
  const map = row.bg_color_by_field ?? {};
  return Object.keys(map)
    .filter((k) => (map as Record<string, string | undefined>)[k])
    .sort()
    .map((k) => `${k}:${(map as Record<string, string | undefined>)[k]}`)
    .join('|');
}

function rowEqual(a: ProgramProgressLikeRow, b: ProgramProgressLikeRow): boolean {
  return (
    (a.unit_text ?? '') === (b.unit_text ?? '') &&
    (a.learning_text ?? '') === (b.learning_text ?? '') &&
    (a.page_text ?? '') === (b.page_text ?? '') &&
    (a.assistant_page_text ?? '') === (b.assistant_page_text ?? '') &&
    (a.current_period_text ?? '') === (b.current_period_text ?? '') &&
    (a.total_period_text ?? '') === (b.total_period_text ?? '') &&
    (a.material_text ?? '') === (b.material_text ?? '') &&
    (a.record_cross_curricular_text ?? '') === (b.record_cross_curricular_text ?? '') &&
    (a.record_memo_text ?? '') === (b.record_memo_text ?? '') &&
    (a.record_evaluation_text ?? '') === (b.record_evaluation_text ?? '') &&
    (a.record_class_text ?? '') === (b.record_class_text ?? '') &&
    (a.record_member_group_id ?? null) === (b.record_member_group_id ?? null) &&
    bgSignature(a) === bgSignature(b)
  );
}

function sameScopeRows(
  existing: Record<string, AnnualProgramDisplayOverlayEntry>,
  scopeId: string,
  incoming: Record<string, ProgramProgressLikeRow>
): boolean {
  const existingEntries = Object.entries(existing).filter(
    ([annualKey, entry]) => !!annualKey.trim() && (entry?.scopeId ?? '').trim() === scopeId && !!entry?.row
  );
  const incomingKeys = Object.keys(incoming)
    .map((k) => k.trim())
    .filter(Boolean);
  if (existingEntries.length !== incomingKeys.length) return false;
  const incomingSet = new Set(incomingKeys);
  for (const [annualKey, entry] of existingEntries) {
    const key = annualKey.trim();
    if (!incomingSet.has(key)) return false;
    const nextRow = incoming[key];
    if (!nextRow) return false;
    if (!rowEqual(trimLikeRow(entry.row), trimLikeRow(nextRow))) return false;
  }
  return true;
}

export function readAnnualProgramDisplayOverlayByAnnualKey(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  /** 있으면 해당 프로그램 스코프 행만 반환(다른 편제가 같은 annualKey를 덮어쓴 잔상 방지) */
  scopeId?: string;
}): Record<string, ProgramProgressLikeRow> {
  const sid = params.workspaceId.trim();
  const gid = params.gradeId.trim();
  if (!sid || !gid) return {};
  const storage = getStorage();
  if (!storage) return {};
  const wantScope = (params.scopeId ?? '').trim();
  const payload = parsePayload(storage.getItem(storageKey(sid, params.academicYear, params.termNumber, gid)));
  const out: Record<string, ProgramProgressLikeRow> = {};
  Object.entries(payload.byAnnualKey).forEach(([annualKey, entry]) => {
    if (!annualKey.trim() || !entry?.row) return;
    if (wantScope && (entry.scopeId ?? '').trim() !== wantScope) return;
    out[annualKey] = trimLikeRow(entry.row);
  });
  return out;
}

/** annualKey → { scopeId, row } — 연간진도표에서 스코프별 오버레이를 가릴 때 */
export function readAnnualProgramDisplayOverlayEntries(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
}): Record<string, { scopeId: string; row: ProgramProgressLikeRow }> {
  const sid = params.workspaceId.trim();
  const gid = params.gradeId.trim();
  if (!sid || !gid) return {};
  const storage = getStorage();
  if (!storage) return {};
  const payload = parsePayload(storage.getItem(storageKey(sid, params.academicYear, params.termNumber, gid)));
  const out: Record<string, { scopeId: string; row: ProgramProgressLikeRow }> = {};
  Object.entries(payload.byAnnualKey).forEach(([annualKey, entry]) => {
    if (!annualKey.trim() || !entry?.row) return;
    out[annualKey] = {
      scopeId: (entry.scopeId ?? '').trim(),
      row: trimLikeRow(entry.row),
    };
  });
  return out;
}

export function upsertAnnualProgramDisplayOverlayForScope(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  scopeId: string;
  byAnnualKey: Record<string, ProgramProgressLikeRow>;
}): void {
  const sid = params.workspaceId.trim();
  const gid = params.gradeId.trim();
  const scopeId = params.scopeId.trim();
  if (!sid || !gid || !scopeId) return;
  const storage = getStorage();
  if (!storage) return;
  const key = storageKey(sid, params.academicYear, params.termNumber, gid);
  const payload = parsePayload(storage.getItem(key));
  if (sameScopeRows(payload.byAnnualKey, scopeId, params.byAnnualKey)) return;
  const nextByAnnualKey: Record<string, AnnualProgramDisplayOverlayEntry> = {};
  Object.entries(payload.byAnnualKey).forEach(([annualKey, entry]) => {
    if (!annualKey.trim()) return;
    if ((entry?.scopeId ?? '').trim() === scopeId) return;
    if (!entry?.row) return;
    nextByAnnualKey[annualKey] = {
      scopeId: (entry.scopeId ?? '').trim(),
      updatedAt: Number(entry.updatedAt ?? 0) || 0,
      row: trimLikeRow(entry.row),
    };
  });
  const now = Date.now();
  Object.entries(params.byAnnualKey).forEach(([annualKey, row]) => {
    const ak = annualKey.trim();
    if (!ak) return;
    nextByAnnualKey[ak] = {
      scopeId,
      updatedAt: now,
      row: trimLikeRow(row),
    };
  });
  try {
    storage.setItem(key, JSON.stringify({ byAnnualKey: nextByAnnualKey } as AnnualProgramDisplayOverlayPayload));
    emitChanged();
  } catch {
    /* ignore */
  }
}
